import { splitSqlStatements } from "@-/com.npmjs.expo-sqlite";
import { classifyTransactionControl } from "#actor-sync-connection-lock";

export const LEADER_SQL_METHODS = [
  "runAsync",
  "getAllAsync",
  "getFirstAsync",
  "execAsync",
  "prepareValuesAsync",
] as const;
export type LeaderSqlMethod = (typeof LEADER_SQL_METHODS)[number];
const SQL_METHOD_NAMES: readonly string[] = LEADER_SQL_METHODS;
const SELECT_RE = /^\s*(?:select|explain)\b/i;
const MAINTENANCE_RE = /^\s*(?:pragma|vacuum|attach|detach)\b/i;

export interface LeaderTrackedCall {
  args: readonly unknown[];
  createdAt: number;
  method: LeaderSqlMethod;
  transactionId: string | null;
  writeId: string;
}

export function parseLeaderTrackedCall(value: unknown): LeaderTrackedCall {
  if (typeof value !== "object" || value === null) {
    throw new TypeError("Invalid tracked SQL call");
  }
  const args: unknown = Reflect.get(value, "args");
  const method: unknown = Reflect.get(value, "method");
  const createdAt: unknown = Reflect.get(value, "createdAt");
  const transactionId: unknown = Reflect.get(value, "transactionId");
  const writeId: unknown = Reflect.get(value, "writeId");
  if (
    typeof method !== "string" ||
    typeof createdAt !== "number" ||
    typeof writeId !== "string" ||
    !Array.isArray(args) ||
    typeof args[0] !== "string" ||
    !SQL_METHOD_NAMES.includes(method) ||
    !Number.isFinite(createdAt) ||
    (transactionId !== null && typeof transactionId !== "string") ||
    writeId.length === 0
  ) {
    throw new TypeError("Invalid tracked SQL call");
  }
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- allowlist validated above
  return { args, createdAt, method: method as LeaderSqlMethod, transactionId, writeId };
}

export function getLeaderCallControl(args: readonly unknown[]) {
  const sql = typeof args[0] === "string" ? args[0] : "";
  return splitSqlStatements(sql).length === 1 ? classifyTransactionControl(sql) : null;
}

/** SELECT and EXPLAIN do not execute mutations. Unknown SQL is not assumed read-only. */
export function isLeaderReadOnlySql(args: readonly unknown[]): boolean {
  const sql = typeof args[0] === "string" ? args[0] : "";
  const statements = splitSqlStatements(sql);
  return statements.length > 0 && statements.every((statement) => SELECT_RE.test(statement));
}

export function isLeaderMaintenanceSql(args: readonly unknown[]): boolean {
  return args.length > 0 && MAINTENANCE_RE.test(String(args[0]));
}
